/**
 * WaiverFlow — THE single shared waiver-signing wizard.
 * Used by BOTH:
 *   - MobileWaiverWizard (customer-ticket overlay, booking mode)
 *   - StandaloneWaiverPage (/sign/{org}, walk-up mode)
 * Maintain the flow HERE — the two wrappers only supply branding, the submit
 * endpoint, and what happens after success.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ChevronLeft, Shield, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'sonner';
import api from '../utils/api';
import PhoneInput from './PhoneInput';

const MAX_NAME_LENGTH = 100;
const MAX_EMAIL_LENGTH = 254;
const MIN_NAME_LENGTH = 2;
const RESEND_COOLDOWN_SECONDS = 15;
const AGE_RANGES = ['Under 18', '18-24', '25-34', '35-44', '45-54', '55+'];

const sanitizeInput = (str, maxLength = 100) => {
  if (!str) return '';
  return str.replace(/<[^>]*>/g, '').replace(/[<>'"&]/g, '').slice(0, maxLength);
};
const sanitizeInputFinal = (str, maxLength = 100) => {
  if (!str) return '';
  return str.replace(/<[^>]*>/g, '').replace(/[<>'"&]/g, '').trim().slice(0, maxLength);
};
const isValidEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return email && re.test(email) && email.length <= MAX_EMAIL_LENGTH;
};

// 6-digit OTP input — supports iOS "from Messages" autofill (one-time-code) and
// pasting: any multi-digit input gets distributed across the boxes, and the
// waiver auto-submits the moment the 6th digit lands.
const CodeInput = ({ value, onChange, disabled = false, onComplete }) => {
  const inputRefs = useRef([]);
  const digits = value.padEnd(6, '').split('').slice(0, 6);

  const commit = (newValue) => {
    onChange(newValue);
    inputRefs.current[Math.min(newValue.length, 5)]?.focus();
    if (newValue.length === 6 && onComplete) setTimeout(() => onComplete(newValue), 100);
  };

  const handleChange = (index, e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (raw.length > 1) {
      // iOS SMS autofill / fast typing dumps multiple digits into one box
      commit((value.slice(0, index) + raw).slice(0, 6));
      return;
    }
    const newDigits = [...digits];
    newDigits[index] = raw;
    const newValue = newDigits.join('');
    onChange(newValue);
    if (raw && index < 5) inputRefs.current[index + 1]?.focus();
    if (newValue.length === 6 && onComplete) setTimeout(() => onComplete(newValue), 100);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) inputRefs.current[index - 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    commit(e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6));
  };

  return (
    <div className="flex gap-2 justify-center mb-6">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <input key={i} ref={(el) => (inputRefs.current[i] = el)}
          type="text" inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          value={digits[i] || ''} onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)} onPaste={handlePaste}
          disabled={disabled} autoFocus={i === 0}
          data-testid={`waiver-otp-digit-${i}`}
          className="h-16 w-[50px] rounded-xl border border-gray-300 bg-gray-50 text-center font-mono text-3xl outline-none transition-all focus:border-blue-600 focus:bg-white focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-gray-100"
        />
      ))}
    </div>
  );
};

/**
 * Props:
 *   waiverText     — the waiver body to display
 *   branding       — { name, logoUrl, brandColor, waiverVersion }
 *   layout         — 'overlay' (fixed full-screen modal) | 'page' (full page)
 *   onClose        — overlay close handler (overlay layout only)
 *   onSubmit       — async (commonPayload) => void; wrapper posts to its endpoint
 *   successAction  — 'reload' (5s countdown then window reload) | 'reset' (walk-up: "Sign Another Waiver")
 */
const WaiverFlow = ({ waiverText, branding = {}, layout = 'overlay', onClose, onSubmit, successAction = 'reload', region = 'USA' }) => {
  const brandColor = branding.brandColor || '#2563EB';
  const isUAE = region === 'UAE';

  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const signaturePadRef = useRef(null);
  const [capturedSignatureData, setCapturedSignatureData] = useState(null);
  const [hasAgreedToWaiver, setHasAgreedToWaiver] = useState(false);
  const [showWaiverText, setShowWaiverText] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState('');
  const [phoneE164, setPhoneE164] = useState('');
  const [phoneValid, setPhoneValid] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const [email, setEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [signing, setSigning] = useState(false);
  const [verificationFailed, setVerificationFailed] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [skippedVerification, setSkippedVerification] = useState(false);
  const [failedMethods, setFailedMethods] = useState([]);
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [ageRange, setAgeRange] = useState('');
  const [marketingOptIn, setMarketingOptIn] = useState(true);
  const [pendingSig, setPendingSig] = useState(null);
  const latestSmsCodeRef = useRef('');
  const latestEmailCodeRef = useRef('');

  const fullPhone = phoneE164;

  useEffect(() => {
    if (resendCooldown > 0) {
      const t = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendCooldown]);

  useEffect(() => {
    if (showSuccess && successAction === 'reload') {
      if (countdown > 0) {
        const t = setTimeout(() => setCountdown(countdown - 1), 1000);
        return () => clearTimeout(t);
      }
      window.location.reload();
    }
  }, [showSuccess, countdown, successAction]);

  const resetAll = () => {
    setStep(1); setName(''); setCapturedSignatureData(null); setHasAgreedToWaiver(false);
    setShowWaiverText(false); setVerificationMethod(''); setPhoneE164(''); setPhoneValid(false); setSmsCode('');
    setEmail(''); setEmailCode(''); setVerificationFailed(false); setResendCooldown(0);
    setSkippedVerification(false); setFailedMethods([]); setNameError(''); setEmailError('');
    setShowSuccess(false); setCountdown(5);
    setAgeRange(''); setMarketingOptIn(true); setPendingSig(null);
    latestSmsCodeRef.current = ''; latestEmailCodeRef.current = '';
  };

  const validateName = (v) => {
    const s = sanitizeInput(v, MAX_NAME_LENGTH);
    if (s.length < MIN_NAME_LENGTH) { setNameError('Name must be at least 2 characters'); return false; }
    if (!/^[a-zA-Z\s\-'.]+$/.test(s)) { setNameError('Name contains invalid characters'); return false; }
    setNameError(''); return true;
  };
  const validateEmail = (v) => {
    if (!isValidEmail(v)) { setEmailError('Please enter a valid email'); return false; }
    setEmailError(''); return true;
  };

  const captureSignatureLightweight = () => {
    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) return null;
    try {
      const canvas = signaturePadRef.current.getCanvas();
      const maxW = 300, maxH = 150;
      let w = canvas.width, h = canvas.height;
      if (w > maxW) { h = Math.round(h * maxW / w); w = maxW; }
      if (h > maxH) { w = Math.round(w * maxH / h); h = maxH; }
      const c2 = document.createElement('canvas'); c2.width = w; c2.height = h;
      const ctx = c2.getContext('2d');
      ctx.fillStyle = 'white'; ctx.fillRect(0, 0, w, h);
      ctx.drawImage(canvas, 0, 0, w, h);
      return c2.toDataURL('image/jpeg', 0.6);
    } catch { return null; }
  };

  const handleSendCode = async () => {
    setSendingCode(true); setVerificationFailed(false);
    try {
      if (verificationMethod === 'phone' || verificationMethod === 'sms') {
        await api.post('/public/sms/send-code', { phoneNumber: fullPhone, channel: 'sms' });
        toast.success('Code sent! Check your messages (including unknown-sender filters).');
      } else if (verificationMethod === 'whatsapp') {
        await api.post('/public/sms/send-code', { phoneNumber: fullPhone, channel: 'whatsapp' });
        toast.success('Code sent! Check your WhatsApp.');
      } else if (verificationMethod === 'email') {
        if (!validateEmail(email)) { setSendingCode(false); return; }
        await api.post('/public/email/send-code', { email: sanitizeInput(email, MAX_EMAIL_LENGTH) });
        toast.success('Email code sent! Check your inbox.');
      }
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setSkippedVerification(false);
      setStep(5);
    } catch (err) {
      console.error('send-code failed:', err.response?.status, err.response?.data || err.message);
      setVerificationFailed(true);
      toast.error(`Failed to send ${verificationMethod === 'email' ? 'email' : verificationMethod === 'whatsapp' ? 'WhatsApp' : 'SMS'} code`);
    } finally { setSendingCode(false); }
  };

  const buildDeviceInfo = () => {
    const safe = (fn) => { try { return fn(); } catch { return 'Unknown'; } };
    return {
      userAgent: safe(() => navigator.userAgent),
      platform: safe(() => navigator.platform),
      screenResolution: safe(() => `${window.screen.width}x${window.screen.height}`),
      timezone: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
      language: safe(() => navigator.language),
    };
  };

  const submitCommon = async (payload) => {
    try {
      await onSubmit(payload);
      setShowSuccess(true);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      if (status === 400) toast.error(`Error: ${detail || 'Please check your information.'}`);
      else if (status === 404) toast.error('Not found. Please refresh the page.');
      else if (status === 403) toast.error('Access denied. Please refresh the page.');
      else if (err.request && !err.response) toast.error('Network error. Please check your connection.');
      else toast.error('Failed to sign waiver. Please try again.');
      throw err;
    }
  };

  const handleSign = async () => {
    setSigning(true);
    try {
      const currentSmsCode = latestSmsCodeRef.current || smsCode;
      const currentEmailCode = latestEmailCodeRef.current || emailCode;
      let verified = false; let verifiedContact = '';

      if ((verificationMethod === 'phone' || verificationMethod === 'sms' || verificationMethod === 'whatsapp') && phoneE164 && currentSmsCode) {
        let res;
        try {
          res = await api.post('/public/sms/verify-code', { phoneNumber: fullPhone, code: currentSmsCode });
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Invalid or expired code. Please try again.');
          setSigning(false); return;
        }
        verified = res.data.valid; verifiedContact = fullPhone;
        if (!verified) { toast.error(res.data.message || 'Invalid SMS code'); setSigning(false); return; }
      } else if (verificationMethod === 'email' && email && currentEmailCode) {
        let res;
        try {
          res = await api.post('/public/email/verify-code', { email: sanitizeInput(email, MAX_EMAIL_LENGTH), code: currentEmailCode });
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Invalid or expired code. Please try again.');
          setSigning(false); return;
        }
        verified = res.data.valid; verifiedContact = email;
        if (!verified) { toast.error(res.data.message || 'Invalid email code'); setSigning(false); return; }
      }

      setPendingSig({
        verified,
        verificationMethod: verificationMethod === 'phone' ? 'sms' : verificationMethod,
        verifiedContact,
        phoneNumber: (verificationMethod === 'sms' || verificationMethod === 'phone' || verificationMethod === 'whatsapp') ? fullPhone : '',
        email: verificationMethod === 'email' ? sanitizeInputFinal(email, MAX_EMAIL_LENGTH) : '',
        idVerificationRequired: false,
      });
      setStep(7);
    } finally { setSigning(false); }
  };

  const handleIdVerificationSign = () => {
    setPendingSig({
      verified: false,
      verificationMethod: 'id',
      verifiedContact: 'ID Required',
      phoneNumber: phoneE164 || '',
      email: email || '',
      idVerificationRequired: true,
    });
    setStep(7);
  };

  const consentText = `I agree that ${branding.name || 'this operator'} may keep my contact details and send me occasional updates and offers. I can unsubscribe anytime.`;

  const finalizeSign = async () => {
    if (!ageRange) { toast.error('Please select your age range'); return; }
    setSigning(true);
    try {
      await submitCommon({
        signerName: sanitizeInputFinal(name, MAX_NAME_LENGTH),
        signatureData: capturedSignatureData || '',
        ...pendingSig,
        deviceInfo: buildDeviceInfo(),
        signedAt: new Date().toISOString(),
        ageRange,
        marketingOptIn,
        marketingConsentText: marketingOptIn ? consentText : '',
      });
    } catch { /* toast handled in submitCommon */ }
    finally { setSigning(false); }
  };

  const wrapperCls = layout === 'overlay'
    ? 'fixed inset-0 bg-white z-50 flex flex-col overflow-y-auto'
    : 'min-h-screen bg-white flex flex-col';

  // ── Success screen ──────────────────────────────────────────────────────────
  if (showSuccess) {
    return (
      <div className={wrapperCls} data-testid="waiver-success">
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center max-w-sm mx-auto">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Waiver Signed!</h2>
            <p className="text-gray-600 mb-2">You have successfully signed the waiver.</p>
            <p className="text-sm text-gray-500 mb-8">Thank you, {name}.</p>
            {verificationMethod === 'id' && (
              <div className="bg-amber-50 rounded-xl p-4 mb-6 text-left">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">ID Required at Check-in</p>
                    <p className="text-xs text-amber-700 mt-1">Please bring a valid government-issued ID for verification.</p>
                  </div>
                </div>
              </div>
            )}
            {successAction === 'reload' ? (
              <Button onClick={() => window.location.reload()} data-testid="waiver-success-done"
                className="w-full h-12 text-white text-lg font-semibold" style={{ background: brandColor }}>
                Done {countdown > 0 && `(${countdown}s)`}
              </Button>
            ) : (
              <Button onClick={resetAll} data-testid="waiver-success-sign-another"
                className="w-full h-12 text-white text-lg font-semibold" style={{ background: brandColor }}>
                Sign Another Waiver
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Main wizard ─────────────────────────────────────────────────────────────
  const progress = (step === 7 ? 6 : Math.min(step, 5)) / 6;

  return (
    <div className={wrapperCls}>
      {/* Branded header + progress */}
      <div className="bg-white p-4 pb-3 sticky top-0 z-10 border-b border-gray-100">
        <div className="max-w-md mx-auto flex justify-between items-center mb-3">
          <div className="flex items-center gap-2">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt={branding.name} className="h-10 w-auto max-w-[160px] object-contain" />
            ) : (
              <span className="font-semibold text-gray-900" style={{ color: brandColor }}>
                {branding.name || 'Sign Waiver'}
              </span>
            )}
          </div>
          {branding.waiverVersion && (
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">v{branding.waiverVersion}</span>
          )}
        </div>
        <div className="max-w-md mx-auto">
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div className="h-1.5 rounded-full transition-all duration-300" style={{ width: `${progress * 100}%`, background: brandColor }} />
          </div>
        </div>
      </div>

      <div className="flex-1 p-4 pb-24 overflow-y-auto">
        <div className="max-w-md mx-auto">

          {/* STEP 1: Waiver agreement */}
          {step === 1 && (
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-bold text-gray-900">Liability Waiver</h2>
              </div>
              {!showWaiverText ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-50 flex items-center justify-center">
                    <Shield className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Please Review the Waiver</h3>
                  <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                    By proceeding, you acknowledge that we collect your signature and device information for legal enforceability.
                  </p>
                  <Button onClick={() => setShowWaiverText(true)} data-testid="waiver-view-btn"
                    className="w-full h-12 text-white text-base font-semibold" style={{ background: brandColor }}>
                    I Agree &amp; View Waiver
                  </Button>
                </div>
              ) : (
                <div>
                  <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700 leading-relaxed mb-4 max-h-72 overflow-y-auto whitespace-pre-wrap break-words">
                    {waiverText || 'Waiver text not configured. Please contact the operator.'}
                  </div>
                  <label className="flex items-start gap-3 mb-4 cursor-pointer group p-3 rounded-xl bg-gray-50 hover:bg-blue-50/60 transition-colors">
                    <input type="checkbox" checked={hasAgreedToWaiver} onChange={(e) => setHasAgreedToWaiver(e.target.checked)}
                      data-testid="waiver-agree-checkbox"
                      className="mt-0.5 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    <span className="text-sm text-gray-700 leading-relaxed">
                      I have read and agree to the <span className="font-semibold">Liability Waiver &amp; Assumption of Risk</span>
                    </span>
                  </label>
                  <Button onClick={() => setStep(2)} disabled={!hasAgreedToWaiver} data-testid="waiver-agree-btn"
                    className="w-full h-12 text-white text-base font-semibold disabled:opacity-50"
                    style={{ background: hasAgreedToWaiver ? brandColor : '#9CA3AF' }}>
                    I Agree to the Liability Waiver
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* STEP 2: Name & signature */}
          {step === 2 && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Your Name &amp; Signature</h2>
              <p className="text-sm text-gray-600 mb-4">Draw your signature and type your full legal name</p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Signature <span className="text-red-500">*</span></label>
                <div className="border border-gray-300 rounded-xl bg-white overflow-hidden">
                  <SignatureCanvas ref={signaturePadRef}
                    canvasProps={{ className: 'w-full h-28', style: { touchAction: 'none' } }} />
                </div>
                <button onClick={() => signaturePadRef.current?.clear()}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors">
                  Clear Signature
                </button>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Legal Name <span className="text-red-500">*</span>
                </label>
                <Input value={name} data-testid="waiver-name-input"
                  onChange={(e) => {
                    const s = sanitizeInput(e.target.value, MAX_NAME_LENGTH); setName(s);
                    if (s.length >= MIN_NAME_LENGTH) validateName(s);
                  }}
                  onBlur={() => name && validateName(name)}
                  placeholder="Enter your full name"
                  className={`h-12 text-base ${nameError ? 'border-red-500' : ''}`}
                  maxLength={MAX_NAME_LENGTH} />
                {nameError && <p className="mt-1 text-xs text-red-600">{nameError}</p>}
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="w-20 h-12 border-gray-200">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button data-testid="waiver-name-continue"
                  onClick={() => {
                    if (validateName(name)) {
                      setCapturedSignatureData(captureSignatureLightweight());
                      setVerificationMethod(''); setFailedMethods([]); setStep(3);
                    }
                  }}
                  disabled={name.trim().length < MIN_NAME_LENGTH}
                  className="flex-1 h-12 text-white text-base font-semibold disabled:opacity-50"
                  style={{ background: brandColor }}>
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* STEP 3: Verification method */}
          {step === 3 && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Verify Your Identity</h2>
              <p className="text-sm text-gray-600 mb-4">Choose how you'd like to receive your verification code</p>

              {!verificationMethod && (
                <div className="space-y-3 mb-4">
                  <button onClick={() => setVerificationMethod('email')} data-testid="waiver-method-email"
                    className="w-full p-4 rounded-xl bg-gray-50 hover:bg-blue-50 transition-colors flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-gray-900">Email</div>
                      <div className="text-sm text-gray-500">Send code to your email</div>
                    </div>
                  </button>
                  {!isUAE && (
                    <button onClick={() => setVerificationMethod('phone')} data-testid="waiver-method-phone"
                      className="w-full p-4 rounded-xl bg-gray-50 hover:bg-green-50 transition-colors flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-gray-900">Text Message</div>
                        <div className="text-sm text-gray-500">Send code via SMS</div>
                      </div>
                    </button>
                  )}
                  <button onClick={() => setVerificationMethod('whatsapp')} data-testid="waiver-method-whatsapp"
                    className="w-full p-4 rounded-xl bg-gray-50 hover:bg-emerald-50 transition-colors flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <svg className="w-5 h-5 text-emerald-600" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-gray-900">WhatsApp</div>
                      <div className="text-sm text-gray-500">Send code via WhatsApp</div>
                    </div>
                  </button>
                </div>
              )}

              {/* Email input */}
              {verificationMethod === 'email' && (<>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address <span className="text-red-500">*</span></label>
                  <Input type="email" value={email} data-testid="waiver-email-input"
                    onChange={(e) => { setEmail(sanitizeInput(e.target.value.toLowerCase(), MAX_EMAIL_LENGTH)); setVerificationFailed(false); if (emailError) setEmailError(''); }}
                    onBlur={() => email && validateEmail(email)} placeholder="your@email.com"
                    className={`h-12 text-base ${emailError ? 'border-red-500' : ''}`} maxLength={MAX_EMAIL_LENGTH} autoComplete="email" />
                  {emailError && <p className="mt-1 text-xs text-red-600">{emailError}</p>}
                </div>
                {verificationFailed && (
                  <div className="bg-red-50 rounded-xl p-3 mb-4">
                    <p className="text-sm text-red-900 font-medium">Failed to send code</p>
                    <p className="text-xs text-red-700 mt-1">Check your email address or try another option below</p>
                  </div>
                )}
                <div className="space-y-2 mb-4">
                  {!failedMethods.includes('phone') && (
                    <button onClick={() => { setVerificationMethod(isUAE ? 'whatsapp' : 'phone'); setVerificationFailed(false); setFailedMethods([...failedMethods, 'email']); }}
                      className="w-full text-sm py-2.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                      Can't receive email? Try {isUAE ? 'WhatsApp' : 'phone'} instead
                    </button>
                  )}
                  {failedMethods.includes('phone') && (
                    <button onClick={() => { setSkippedVerification(true); setVerificationMethod('id'); setStep(6); }}
                      className="w-full text-sm py-2.5 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors">
                      Can't receive verification? Verify with ID instead
                    </button>
                  )}
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => { setVerificationMethod(''); setVerificationFailed(false); }} className="w-20 h-12 border-gray-200">
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <Button onClick={handleSendCode} disabled={sendingCode || !isValidEmail(email)} data-testid="waiver-send-code"
                    className="flex-1 h-12 text-white text-base font-semibold disabled:opacity-50" style={{ background: brandColor }}>
                    {sendingCode ? <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" />Sending...</span> : 'Send Code'}
                  </Button>
                </div>
              </>)}

              {/* Phone input — country code select + digits only (SMS or WhatsApp) */}
              {(verificationMethod === 'phone' || verificationMethod === 'whatsapp') && (<>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {verificationMethod === 'whatsapp' ? 'WhatsApp Number' : 'Phone Number'} <span className="text-red-500">*</span>
                  </label>
                  <div data-testid="waiver-phone-input">
                    <PhoneInput
                      value={phoneE164}
                      defaultCountry={isUAE ? 'AE' : 'US'}
                      onChange={(e164, meta) => { setPhoneE164(e164 || ''); setPhoneValid(!!meta?.isValid); setVerificationFailed(false); }}
                    />
                  </div>
                  <p className="mt-1.5 text-xs text-gray-400">
                    We&rsquo;ll {verificationMethod === 'whatsapp' ? 'WhatsApp' : 'text'} you a 6-digit code.
                  </p>
                </div>
                {verificationFailed && (
                  <div className="bg-red-50 rounded-xl p-3 mb-4">
                    <p className="text-sm text-red-900 font-medium">Failed to send code</p>
                    <p className="text-xs text-red-700 mt-1">Check your number or try another option below</p>
                  </div>
                )}
                <div className="space-y-2 mb-4">
                  {!failedMethods.includes('email') && (
                    <button onClick={() => { setVerificationMethod('email'); setVerificationFailed(false); setFailedMethods([...failedMethods, 'phone']); }}
                      className="w-full text-sm py-2.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                      Can't receive {verificationMethod === 'whatsapp' ? 'WhatsApp' : 'SMS'}? Try email instead
                    </button>
                  )}
                  {failedMethods.includes('email') && (
                    <button onClick={() => { setSkippedVerification(true); setVerificationMethod('id'); setStep(6); }}
                      className="w-full text-sm py-2.5 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors">
                      Can't receive verification? Verify with ID instead
                    </button>
                  )}
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => { setVerificationMethod(''); setVerificationFailed(false); }} className="w-20 h-12 border-gray-200">
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <Button onClick={handleSendCode} disabled={sendingCode || !phoneValid} data-testid="waiver-send-code"
                    className="flex-1 h-12 text-white text-base font-semibold disabled:opacity-50" style={{ background: brandColor }}>
                    {sendingCode ? <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" />Sending...</span> : 'Send Code'}
                  </Button>
                </div>
              </>)}

              {!verificationMethod && (
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setStep(2)} className="w-20 h-12 border-gray-200">
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* STEP 5: Enter OTP */}
          {step === 5 && (
            <div>
              <div className="mb-6 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-xl font-bold text-gray-900 mb-1">Enter Verification Code</h2>
                  <p className="text-sm text-gray-600">
                    6-digit code sent {verificationMethod === 'whatsapp' ? 'via WhatsApp ' : ''}to <span className="font-medium break-all">{verificationMethod === 'email' ? email : fullPhone}</span>
                  </p>
                </div>
                <button onClick={handleSendCode} disabled={resendCooldown > 0 || sendingCode} data-testid="waiver-resend-code"
                  className="mt-1 shrink-0 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors disabled:text-gray-400 disabled:cursor-not-allowed">
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
              <CodeInput
                value={verificationMethod === 'email' ? emailCode : smsCode}
                onChange={(code) => {
                  if (verificationMethod === 'email') { setEmailCode(code); latestEmailCodeRef.current = code; }
                  else { setSmsCode(code); latestSmsCodeRef.current = code; }
                }}
                onComplete={(completed) => {
                  if (verificationMethod === 'email') latestEmailCodeRef.current = completed;
                  else latestSmsCodeRef.current = completed;
                  if (!signing && completed.length === 6) handleSign();
                }}
                disabled={signing}
              />
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(3)} className="w-20 h-12 border-gray-200">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button onClick={handleSign} data-testid="waiver-sign-btn"
                  disabled={signing || (verificationMethod === 'email' ? emailCode.length !== 6 : smsCode.length !== 6)}
                  className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-white text-base font-semibold disabled:opacity-50">
                  {signing ? <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" />Signing...</span> : 'Sign Waiver'}
                </Button>
              </div>
              <div className="mt-5 text-center text-sm text-gray-500">
                Can&rsquo;t receive the code?{' '}
                {verificationMethod === 'email' ? (
                  <button data-testid="waiver-switch-method"
                    onClick={() => { setVerificationMethod(isUAE ? 'whatsapp' : 'phone'); setFailedMethods([...failedMethods, 'email']); setStep(3); }}
                    className="font-medium text-blue-600 hover:text-blue-800 transition-colors">
                    Try {isUAE ? 'WhatsApp' : 'SMS'}
                  </button>
                ) : (
                  <button data-testid="waiver-switch-method"
                    onClick={() => { setVerificationMethod('email'); setFailedMethods([...failedMethods, 'phone']); setStep(3); }}
                    className="font-medium text-blue-600 hover:text-blue-800 transition-colors">
                    Try email
                  </button>
                )}
                <span className="mx-1.5 text-gray-300">·</span>
                <button data-testid="waiver-id-fallback"
                  onClick={() => { setSkippedVerification(true); setVerificationMethod('id'); setStep(6); }}
                  className="font-medium text-blue-600 hover:text-blue-800 transition-colors">
                  Verify with ID
                </button>
              </div>
            </div>
          )}

          {/* STEP 6: ID verification fallback */}
          {step === 6 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
                <h2 className="text-xl font-bold text-amber-900">ID Verification Required</h2>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-gray-800 mb-3">No problem! Our staff will verify your identity when you arrive.</p>
                <p className="text-sm text-gray-800 mb-3"><strong>Staff may take a photo of your government-issued ID.</strong> This serves as verification for the signed waiver.</p>
                <div className="flex items-start gap-2 pt-2 border-t border-amber-200">
                  <Shield className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                  <p className="text-sm font-medium text-amber-900">Bring a valid ID (driver license, passport, or state ID)</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(skippedVerification ? 3 : 5)} className="w-20 h-12 border-gray-200">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button onClick={handleIdVerificationSign} disabled={signing} data-testid="waiver-id-sign-btn"
                  className="flex-1 h-12 bg-amber-600 hover:bg-amber-700 text-white text-base font-semibold disabled:opacity-50">
                  I Understand — Continue
                </Button>
              </div>
            </div>
          )}

          {/* STEP 7: Age range + marketing consent — final step */}
          {step === 7 && (
            <div data-testid="waiver-final-step">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <h2 className="text-xl font-bold text-gray-900">One Last Step</h2>
              </div>
              <p className="text-sm text-gray-600 mb-5">
                {pendingSig?.verified ? 'Identity verified.' : 'Almost done.'} Confirm your age range to complete your waiver.
              </p>
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Age Range <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-3 gap-2">
                  {AGE_RANGES.map((r) => (
                    <button key={r} onClick={() => setAgeRange(r)} data-testid={`waiver-age-${r.replace(/[^a-zA-Z0-9]/g, '')}`}
                      className={`h-11 rounded-xl text-sm font-semibold transition-colors ${ageRange === r ? 'text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}
                      style={ageRange === r ? { background: brandColor } : {}}>
                      {r}
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex items-start gap-3 mb-6 cursor-pointer p-3.5 rounded-xl bg-gray-50 hover:bg-blue-50/60 transition-colors">
                <input type="checkbox" checked={marketingOptIn} onChange={(e) => setMarketingOptIn(e.target.checked)}
                  data-testid="waiver-optin-checkbox"
                  className="mt-0.5 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-[13px] text-gray-600 leading-relaxed">
                  Keep me in the loop — I agree that <span className="font-semibold text-gray-800">{branding.name || 'this operator'}</span> may keep my contact details and send me occasional updates and offers. Unsubscribe anytime.
                </span>
              </label>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(pendingSig?.verificationMethod === 'id' ? 6 : 5)} className="w-20 h-12 border-gray-200">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button onClick={finalizeSign} disabled={signing || !ageRange} data-testid="waiver-complete-btn"
                  className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-white text-base font-semibold disabled:opacity-50">
                  {signing ? <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" />Signing...</span> : 'Complete & Sign Waiver'}
                </Button>
              </div>
              <p className="mt-4 text-center text-[11px] text-gray-400">
                By signing, you confirm your age range is accurate. Your consent choice is recorded with this waiver.
              </p>
            </div>
          )}

        </div>
      </div>

      {layout === 'overlay' && onClose && (
        <button onClick={onClose} aria-label="Close" data-testid="waiver-close"
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors z-20">
          ✕
        </button>
      )}
    </div>
  );
};

export default WaiverFlow;
